import { dictationLogsSpotlightPorts } from '@/composition/hosts/dictation/spotlights/dictation-sidebar-spotlight-ports';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { SidebarMenuButton, SidebarMenuItem } from '@heidi/design-system/page-shell';
import { DictationLogsSpotlight } from '@heidi/dictate';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { MoreHorizontal } from 'lucide-react';
import { type ReactNode } from 'react';

import { useFooterRailState } from './footer-rail-context';
import { FooterRailMoreRow } from './footer-rail-more-row';
import { FOOTER_MORE_TEST_ID } from './footer-rail.constants';

/**
 * Re-hosts Dictate's queued spotlight on the always-mounted More tile when Dictate
 * is collapsed. `useQueuedModal` only dequeues from a mounted component, so a
 * spotlight inside a closed dropdown never mounts and jams the launch-modal queue
 * app-wide. Collapsed Dictate renders nothing in place, so this keeps exactly one
 * mount, never two.
 */
function FooterRailMoreSpotlights({
  hostsDictate,
  children,
}: {
  hostsDictate: boolean;
  children: ReactNode;
}) {
  if (hostsDictate) {
    // asChild keeps the More <li> a direct child of the shared <ul> (no wrapper div).
    return (
      <DictationLogsSpotlight asChild {...dictationLogsSpotlightPorts}>
        {children}
      </DictationLogsSpotlight>
    );
  }
  return <>{children}</>;
}

export function FooterRailMore() {
  const { moreIds, moreDescriptors, moreLabel } = useFooterRailState();

  // Key the tile off `moreIds`, not `moreDescriptors`: on a remount, membership (and
  // so `moreIds`) is seeded synchronously but descriptors fill in from a post-paint
  // effect, so gating on descriptors would drop the tile for the first frame — a
  // one-tile layout shift.
  if (moreIds.length === 0) return null;

  const isActive = moreDescriptors.some((descriptor) => descriptor.isActive);

  return (
    <FooterRailMoreSpotlights hostsDictate={moreIds.includes('dictate')}>
      {/* Bare item — first child of the shared ribbon-rail <ul> in SideNavFooter. */}
      <SidebarMenuItem>
        {/* modal={false}: no scroll-lock/focus-trap, matching the footer Help dropdown. */}
        <DropdownMenu
          modal={false}
          onOpenChange={(open) => {
            if (!open) return;
            track({
              event_name: TrackingEventName.ButtonClicked,
              props: {
                screen_name: ScreenName.Sidenav,
                action: ButtonAction.SidebarFooterMoreOpened,
                label: 'sidebar_footer_more',
              },
              options: { amplitude: false },
            });
          }}
        >
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              layout="iconOnly"
              isActive={isActive}
              tooltip={moreLabel}
              aria-label={moreLabel}
              data-testid={FOOTER_MORE_TEST_ID}
            >
              <MoreHorizontal />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="right"
            align="end"
            sideOffset={12}
            collisionPadding={{ top: 8, bottom: 8 }}
            onCloseAutoFocus={(event) => event.preventDefault()}
            className="min-w-52 rounded-lg"
          >
            {moreDescriptors.map((descriptor) => (
              <FooterRailMoreRow key={descriptor.id} descriptor={descriptor} />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </FooterRailMoreSpotlights>
  );
}
